import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Routes, Route } from "react-router";
import { ReactiveCore, DexieDatabaseProvider, useSuccessReactor } from "@livefree/reactive";
import { dexieRequestBrokerAddon } from "@livefree/request-broker";
import { EnvironmentConfigurations } from "@livefree/runtime";
import { App, createEmbed } from "@livefree/applications";
import { Card, Checkbox, Chip, Separator, Spinner, Breadcrumbs, BreadcrumbsItem } from "@heroui/react";
import { useMemo, useState, useEffect, createElement } from "react";
import { Uniformly, AddressLookupField } from "@livefree/uniformly";
import { GoogleMapCircle, GoogleMapMarker, GoogleMap, withModalController } from "@livefree/react-ui";
import { RiSignalTowerLine, RiMapPinFill } from "@remixicon/react";
const reactive = new ReactiveCore("cellular");
const CellularConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "CellularAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "CellularAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "CellularAPI"
    }
  }
});
class TowersClient extends ArtemisSDK.TowersClient {
  getCellularTowers(metadata) {
    const { address, radius } = metadata;
    return super.getAllTowers(address, radius);
  }
}
const TowersService = reactive.service("towers", TowersClient, {
  recursive: true,
  recurseDepth: 2
})((Service) => new Service(CellularConfig));
TowersService.reactor("towersReactor", {
  success: (response, service, db) => {
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.cellular.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => {
      db.version(2).stores(storeFormat(_store));
    }
  }
};
const _store = {
  towers: ["&id"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const Layout = ({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
};
const BaseLayout = reactive.configure(ReactiveConfig).render((props) => /* @__PURE__ */ jsx(Layout, { ...props }));
const OperatorType = {
  ATT: "AT&T",
  TMobile: "T-Mobile"
};
const OperatorCircleColor = {
  [OperatorType.ATT]: "#4285F4",
  [OperatorType.TMobile]: "#E30075"
};
const OperatorImage = {
  [OperatorType.ATT]: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAIGNIUk0AAHomAACAhAAA+gAAAIDoAAB1MAAA6mAAADqYAAAXcJy6UTwAAALfUExURQAAAACm3gCo4ACk3QCp4AC4/wCo3wCn4ACt4wCo4QCk3ACp4QCr5gCq5ADM/wCq4wCl3gCm3ACq4QAfLACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACn4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACn3wCo4ACo4ACo4ACo4ACo4ACo4ACo3wCo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACp3QCo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACn4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo3wCo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo3wCo4ACo4ACo4ACo4ACo4ACo4ACn4ACo4ACn3wCo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4ACo4P///4wTmTgAAADzdFJOUwAAAAAAAAAAAAAAAAAAAAAAAAAAAiptrdrz/fLZrGsoAQI4meD8+96VNAESh+vpfwoDW90fOiK6ylBNPTnCJ1yn1e34/vTlyVUUK8/XMRPB+koDFi9GVl9hTzwlD1nTuSTFIGDxBIV7NgkGCxVzqiEBHHLwvq+1w+f3aAwFLsv1U80RP3epzub5ogHhY6aMJlJudJyRaagbgQeYSBgIO6XbMsjWq4NlSbssNbadM2yj9uOCQuyPQW+AlJJ1Xe7MepsdkOLRYg5x7zCIk8ZRjeSynrcBvFrY6NJmDSMCmrQ3QL92DXkIVxo+ZExYfXhqfqTq0Cnf5soAAAABYktHRPQy1StNAAAACXBIWXMAAA7DAAAOwwHHb6hkAAAAB3RJTUUH6QcXFg4bjjxofAAAAyBJREFUOMtjYAABRiYmJhFRMXEJSSkpaRlZOXkFoAAzAwIwMSkqKauofoYCNXUNTS0mJiRpJm0d3c8oQE/fgAmmhImJxdDoMzowNmGFqmBiYzIVNTNXRZFWs7BksrJmYmcHG2Bja8ekbe/g6OQs9fmzi5qrm7uyhyeTl7ePL9gIJj//gECgaUHBIaFh4RH2kVHRMUxMBrFxnz/HxzCBDEhw+5zoxYQMgpKS452BFqVoAo1g4khNk/oskZ4RnJkaFJSVnSMfmpunLgVxaH4BE0NWIZNBkcrnz8WOJaV54mXljhWVMJdKVlVH1jDUhgPNlNevq0f3p2qdB2dDYzRDUUkTyFrt5pbWtnZXqGc7nOI6Y7W7wrpVexh6P/dlMzGxg5ym3W89oWfipEli4YEhfkxcmpMlP3+ewjC1UmqarxYobpD9wTI9zAEc+DMYZqa7fq6YNXtOEEyOe+70eXLzF6iB7VJfyMAT1LDI5bPz4iVLl01YvnxCT7ryCglJmEPdGrwYVjIx5fTEO4Hd3dGBEiPFS1bJr2ZYA/KEwdp16xsrNrjA5Vw2bCzZpJS5uW0Lg+PWTLDVik1222K37+jbOWvWLp3de7aZzWSy27r38z6G/RsOHGTCAmbaHDr8GeTNbJ2OI0eDIYJsMI8cW3s8/gTIKomTDEw1PeouR04tNztWo8XLJ5IVkzTv6OkzThCnqJzlZ2AyZOo/d/6z6nn3C+snX5x2qU4F7snKUqtVl4HppZ+J267oClqi/dyh3hm48NxKYHoQmW05E+jRlVdPiS+6tvG63vXzxkdKOtMDC3MibrSwABUwZd08vQ3s1dRbc/LtROVvT2+qYbpze90itbvZTOA0J2JVdq/ofk4N1A9a2SYPckvdPp+YcQua7oGBXfXwsOMl5aWPcls27VxyphuYIPXmPwDGMQs457AJMD2ePu/JBZm9HWD3SaVck919dtVTROZjYrq17dn96c9FX1juefnS6JW19etHHtpM7IJImTPo5Ju8aYfeViVHvHuvfG/10pDLTEi5lwEUxl0JLz8c+ejqqvepM8JMCCggDJYCAAVyqSJMyaY7AAAAJXRFWHRkYXRlOmNyZWF0ZQAyMDI1LTA3LTIzVDIyOjE0OjE1KzAwOjAwJxofKQAAACV0RVh0ZGF0ZTptb2RpZnkAMjAyNS0wNy0yM1QyMjoxNDoxNSswMDowMFZHp5UAAAAodEVYdGRhdGU6dGltZXN0YW1wADIwMjUtMDctMjNUMjI6MTQ6MjcrMDA6MDAYQpCAAAAAAElFTkSuQmCC",
  [OperatorType.TMobile]: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAIGNIUk0AAHomAACAhAAA+gAAAIDoAAB1MAAA6mAAADqYAAAXcJy6UTwAAAEdUExUReIAdOIAc+EAcuIAcuIBdOlCmO9zsu9xsu9ysulBl+IBdfOVxf////76/P74+/OTxPOUxf/9/vnK4vGBuutQoO1cpv3u9uxapetOn/GAuvOSxOUaguEAceYjh/3p8/zo8+Yhhv/6/Pau0+QOfOYmifzp8+YkiOQPfOpLnepMnf75+/OXxvvZ6uUbg+UdhPvb6/OYx/nG4OMKeeMLevnI4fOWxug2kepEmeICdeg1keMMe+QVf+QUf+QRffB5tvrT5/rQ5frS5vax1OUYgfvc7OUehO1gqPWnz/WlzvWmz/KMwOQTfuYnif3q8+YliOgzkPB3tf/8/u91tOMGd+gxj+pHm/GCu/zo8vzn8upGmvnN5P77/fnM4+1fqO1ep0c9NHMAAAABYktHRAyBs1FjAAAAB3RJTUUH6QcWFTsUBkhuZwAAAUdJREFUOMu9k1lTwjAUhZMGEeltBVSUFlFxFyhulVJk38EdXFH//88wBTtNaju+cR4yOck3kztnThCal7BAXBIwex9YCC5ShULOGlxigbAIliTZWmVpapbDLBABiMZWVtfiAPH1jUQsCqC4AElRcXIzBbC1ncSqsvMHSO9ihPcosH9AN4dpFyDKRwQ5ACLHJ/wMmWwOswDW8iIHnJ6dIxZAF5c6C6CrgsEDRqHIJUmDw+yQmEbLAWbpulQ2bMAoU2tySVaqtXqjSWYAaTbqtWqFG1Kn2eY1YQYIWp5a3R01ZB0gS22Ez4GetNo20G5Rm2GBQKfb6w+G9gzDQb/X7QQ8auPk4NOrf4GbW4DUnT9AilZh7onfNcEPtEmPT5h4IqPx88urVca3xPt45PF+7mMCv5p8qh5zmF/T6k8r/216vcF+GzRP/QBszStQJ8l5XgAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyNS0wNy0yMlQyMTo1ODo1OCswMDowMHvAHkAAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjUtMDctMjJUMjE6NTg6NTgrMDA6MDAKnab8AAAAKHRFWHRkYXRlOnRpbWVzdGFtcAAyMDI1LTA3LTIyVDIxOjU5OjIwKzAwOjAwi2CrYwAAAABJRU5ErkJggg=="
};
const OperatorIDs = {
  [OperatorType.ATT]: [1, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
  [OperatorType.TMobile]: [2, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30]
};
const Carriers = [
  { id: "att", label: "AT&T", operator: OperatorType.ATT, color: "#00A8E0" },
  { id: "tmobile", label: "T-Mobile", operator: OperatorType.TMobile, color: "#E20074" }
];
const TowerCircle = ({ center, radius, operator }) => /* @__PURE__ */ jsx(
  GoogleMapCircle,
  {
    center,
    radius,
    strokeColor: OperatorCircleColor[operator],
    fillColor: OperatorCircleColor[operator]
  }
);
const getOperatorType = (operatorId) => {
  return Object.keys(OperatorIDs).find((key) => OperatorIDs[key].includes(operatorId));
};
function TowerItem({ tower }) {
  const position = { lat: tower.lat, lng: tower.lon };
  const operatorType = getOperatorType(tower.operatorId);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(GoogleMapMarker, { position, icon: OperatorImage[operatorType] }),
    /* @__PURE__ */ jsx(TowerCircle, { center: position, radius: tower.range, operator: operatorType })
  ] });
}
const CarrierDot = ({ color }) => /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "size-2.5 flex-none rounded-full", style: { background: color } });
const CoverageMapPanel = ({
  address = null,
  carriers = {},
  towers = [],
  checking = false,
  onCarriersChange = () => {
  }
}) => {
  const towerMarkers = useMemo(() => {
    if (!towers?.length) return null;
    return towers.map((tower) => /* @__PURE__ */ jsx(TowerItem, { tower }, `${tower.lat}-${tower.lon}-${tower.operatorId}`));
  }, [towers]);
  const activeCarriers = Carriers.filter((c) => carriers[c.id]);
  return /* @__PURE__ */ jsxs(Card, { className: "w-full h-full gap-0 overflow-hidden p-0", children: [
    /* @__PURE__ */ jsxs(Card.Header, { className: "flex flex-row flex-wrap items-center gap-4 p-3", children: [
      Carriers.map((c) => /* @__PURE__ */ jsx(
        Checkbox,
        {
          isSelected: !!carriers[c.id],
          onChange: (on) => onCarriersChange({ ...carriers, [c.id]: on }),
          children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
            /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
            /* @__PURE__ */ jsx(CarrierDot, { color: c.color }),
            c.label
          ] })
        },
        c.id
      )),
      /* @__PURE__ */ jsxs(Chip, { color: "accent", size: "sm", children: [
        /* @__PURE__ */ jsx(RiSignalTowerLine, { size: 12 }),
        towers.length,
        " towers found"
      ] }),
      checking && /* @__PURE__ */ jsx(Chip, { color: "warning", size: "sm", children: "Pinging towers…" })
    ] }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsxs("div", { className: "relative h-full", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: `h-full transition-opacity duration-200 ${checking ? "opacity-40" : "opacity-100"}`,
          children: /* @__PURE__ */ jsx(GoogleMap, { classNames: { wrapper: "w-full h-full" }, address, children: towerMarkers })
        }
      ),
      checking && /* @__PURE__ */ jsx("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs(Chip, { size: "lg", children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        "Pinging towers…"
      ] }) }),
      /* @__PURE__ */ jsxs("div", { className: "absolute bottom-3 left-3 flex gap-2", children: [
        activeCarriers.map((c) => /* @__PURE__ */ jsxs(Chip, { size: "sm", children: [
          /* @__PURE__ */ jsx(CarrierDot, { color: c.color }),
          c.label
        ] }, c.id)),
        /* @__PURE__ */ jsxs(Chip, { size: "sm", children: [
          /* @__PURE__ */ jsx(RiMapPinFill, { size: 12, color: "#DC2626" }),
          "Service address"
        ] })
      ] })
    ] })
  ] });
};
const EARTH_RADIUS_METERS = 6371e3;
const toRadians = (degrees) => degrees * Math.PI / 180;
const distanceInMeters = (a, b) => {
  const dLat = toRadians(b.lat - a.lat);
  const dLon = toRadians(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
};
const coverageByCarrier = (towers, point) => {
  if (point?.lat == null || point?.lon == null) return null;
  return Object.fromEntries(
    Carriers.map((c) => [
      c.id,
      (towers ?? []).some(
        (tower) => OperatorIDs[c.operator]?.includes(tower.operatorId) && distanceInMeters(point, tower) <= tower.range
      )
    ])
  );
};
const DEFAULT_CARRIERS = { att: true, tmobile: true };
const CoverageMapController = reactive.component(
  ({
    services,
    monitors,
    address,
    carriers,
    onCarriersChange,
    onTowersChange,
    onCoverageChange
  }) => {
    const [ownCarriers, setOwnCarriers] = useState(DEFAULT_CARRIERS);
    const activeCarriers = carriers ?? ownCarriers;
    const setCarriers = onCarriersChange ?? setOwnCarriers;
    const [towers, setTowers] = useState([]);
    useEffect(() => {
      if (!address?.address1) return;
      onCoverageChange?.(null);
      services.towers.getCellularTowers({
        address: `${address.address1} ${address.city}, ${address.state} ${address.postalCode}`,
        radius: 1e4
      });
    }, [address?.address1, address?.city, address?.state, address?.postalCode]);
    useSuccessReactor(services.towers.getCellularTowers, (payload) => {
      setTowers(payload.towers ?? []);
      onCoverageChange?.(coverageByCarrier(payload.towers, payload.address));
    });
    const filteredTowers = useMemo(() => {
      const enabled = Carriers.filter((c) => activeCarriers[c.id]).map((c) => c.operator);
      return (towers ?? []).filter(
        (tower) => enabled.some((operator) => OperatorIDs[operator]?.includes(tower.operatorId))
      );
    }, [towers, activeCarriers]);
    useEffect(() => {
      onTowersChange?.(filteredTowers);
    }, [filteredTowers]);
    return /* @__PURE__ */ jsx(
      CoverageMapPanel,
      {
        address,
        carriers: activeCarriers,
        towers: filteredTowers,
        checking: monitors.getCellularTowers.executing,
        onCarriersChange: setCarriers
      }
    );
  },
  {
    name: "CoverageMapController",
    monitors: (services) => [services.towers.getCellularTowers]
  }
);
const TowersController = () => {
  const [address, setAddress] = useState(null);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Uniformly, { className: "mb-4", onChange: ({ data }) => setAddress(data.address ?? null), children: /* @__PURE__ */ jsx(
      AddressLookupField,
      {
        name: "address",
        label: "Address",
        placeholder: "Enter Address",
        variant: "faded"
      }
    ) }),
    /* @__PURE__ */ jsx(CoverageMapController, { address })
  ] });
};
TowersController.Modal = withModalController(TowersController);
const TowersView = () => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Cellular" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Coverage Map" })
    ] }) }),
    /* @__PURE__ */ jsx(TowersController, {})
  ] });
};
const TowersRouter = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(TowersView, {}) }) });
const Router = () => /* @__PURE__ */ jsx(BaseLayout, { children: /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "map/*", element: /* @__PURE__ */ jsx(TowersRouter, {}) }) }) });
function getCellularNavigation() {
  return {
    isVisible: false,
    items: []
  };
}
function getCellularResources(_runtime) {
  const items = [];
  return {
    items,
    handlers: () => ({})
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const COVERAGE_MAP = "cellular.coverage-map";
const CoverageMap = (props) => createElement(
  Embed,
  null,
  createElement(CoverageMapController, props)
);
let appRuntime = null;
const initCellularRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("cellular", {
    description: {
      title: "Telecommunications",
      iconClass: "ri-broadcast-line",
      defaultRoute: "/map",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getCellularNavigation,
    resources: getCellularResources,
    config: () => CellularConfig.current
  });
  hostRuntime.components.register(COVERAGE_MAP, CoverageMap);
};
export {
  initCellularRuntime
};
//# sourceMappingURL=index.js.map
